import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import type { ReactNode } from 'react';
import { createContext, useCallback, useMemo } from 'react';
import type { Permission } from '../permissions';

interface PermissionContext {
  permissions: Permission[];
  isAuthenticated: boolean;
  isAccessAllowed: (permission: Permission | Permission[]) => boolean;
  hasAllPermissions: (permissions: Permission[]) => boolean;
}

export const PermissionsContext = createContext<PermissionContext | null>(null);

interface PermissionsProviderProps {
  children: ReactNode;
}

const PermissionsProvider = ({ children }: PermissionsProviderProps) => {
  const { user, session } = useUser();

  const isAccessAllowed = useCallback(
    (permission: Permission | Permission[]) => {
      if (!user?.grantedPermissions) return false;

      const permissions = Array.isArray(permission) ? permission : [permission];
      return permissions.some((p) => user.grantedPermissions.includes(p));
    },
    [user?.grantedPermissions]
  );

  const hasAllPermissions = useCallback(
    (permissions: Permission[]) => {
      if (!user?.grantedPermissions) return false;
      return permissions.every((p) => user.grantedPermissions.includes(p));
    },
    [user?.grantedPermissions]
  );

  const value = useMemo(
    () => ({
      permissions: user?.grantedPermissions || [],
      isAuthenticated: !!session,
      isAccessAllowed,
      hasAllPermissions,
    }),
    [user?.grantedPermissions, session, isAccessAllowed, hasAllPermissions]
  );

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
};

export default PermissionsProvider;
